import React, { useContext, useEffect, useState, useRef } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material'
import {
  ArrowBack,
  Send,
  AttachFile,
  AccessTime,
  Person,
  Category,
  Close,
  InsertDriveFile,
  Verified,
  Mic,
  Stop,
  FiberManualRecord,
  Download,
  CheckCircle,
  Refresh,
  Reply,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  PictureAsPdf,
  TableChart,
  Description,
  FolderZip,
  TextSnippet,
  PlayArrow,
  Pause,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import Context from '../../../contexts/Context'
import moment from 'moment'
import 'moment/locale/pt-br'
import useAudioRecorder, { isAudioFile, isAudioMimeType } from '../../../hooks/useAudioRecorder'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import MarkdownEditor from '../../../components/Ticket/MarkdownEditor'

const formatAudioTime = (seconds) => {
  if (!seconds || !isFinite(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const AudioPlayer = ({ src }) => {
  const audioRef = React.useRef(null)
  const [playing, setPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const durationProbed = React.useRef(false)

  const updateDuration = () => {
    const d = audioRef.current?.duration
    if (d && isFinite(d) && d > 0) setDuration(d)
  }

  // WebM/Ogg do MediaRecorder não têm duração no header.
  // Forçar o browser a revelar pulando para o final e voltando.
  const probeDuration = () => {
    const audio = audioRef.current
    if (!audio || durationProbed.current) return
    if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration)
      return
    }
    durationProbed.current = true
    const onDurChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
        audio.currentTime = 0
        audio.removeEventListener('durationchange', onDurChange)
      }
    }
    audio.addEventListener('durationchange', onDurChange)
    audio.currentTime = 1e101 // pular para o "final" revela a duração
  }

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.addEventListener('loadedmetadata', probeDuration)
    audio.addEventListener('canplay', probeDuration)
    // Tentar já se loaded
    if (audio.readyState >= 1) probeDuration()
    return () => {
      audio.removeEventListener('loadedmetadata', probeDuration)
      audio.removeEventListener('canplay', probeDuration)
    }
  }, [src])

  const toggle = () => {
    if (!audioRef.current) return
    if (playing) { audioRef.current.pause() } else { audioRef.current.play() }
    setPlaying(!playing)
  }

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
      borderRadius: 6, bgcolor: '#F1F5F9', border: '1px solid #E2E8F0', maxWidth: 320,
    }}>
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        onTimeUpdate={() => {
          setCurrentTime(audioRef.current?.currentTime || 0)
          updateDuration()
        }}
        onEnded={() => setPlaying(false)}
      />
      <IconButton size="small" onClick={toggle} sx={{ bgcolor: '#0073ea', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: '#005bb5' } }}>
        {playing ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 100 }}>
        <Box
          component="input"
          type="range"
          min={0}
          max={duration || 0}
          value={currentTime}
          onChange={(e) => { if (audioRef.current) audioRef.current.currentTime = e.target.value }}
          sx={{
            width: '100%', height: 4, appearance: 'none', bgcolor: '#CBD5E1', borderRadius: 2, outline: 'none', cursor: 'pointer',
            '&::-webkit-slider-thumb': { appearance: 'none', width: 12, height: 12, borderRadius: '50%', bgcolor: '#0073ea', cursor: 'pointer' },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace', fontSize: 11, minWidth: 70, textAlign: 'right' }}>
        {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
      </Typography>
    </Box>
  )
}

const STATUS_COLORS = {
  OPEN: { bg: '#DBEAFE', color: '#1E40AF', label: 'Aberto' },
  IN_PROGRESS: { bg: '#FEF3C7', color: '#92400E', label: 'Em Andamento' },
  WAITING_CLIENT: { bg: '#FEE2E2', color: '#991B1B', label: 'Aguardando Cliente' },
  RESOLVED: { bg: '#D1FAE5', color: '#065F46', label: 'Finalizado' },
}

const PRIORITY_COLORS = {
  LOW: { bg: '#F3F4F6', color: '#6B7280', label: 'Baixa' },
  MEDIUM: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Média' },
  HIGH: { bg: '#FEF3C7', color: '#D97706', label: 'Alta' },
  URGENT: { bg: '#FEE2E2', color: '#DC2626', label: 'Urgente' },
}

const getFileTypeInfo = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase()
  if (['pdf'].includes(ext)) return { icon: PictureAsPdf, color: '#DC2626', bg: '#FEE2E2', label: 'PDF' }
  if (['doc', 'docx'].includes(ext)) return { icon: Description, color: '#2563EB', bg: '#DBEAFE', label: 'DOC' }
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { icon: TableChart, color: '#16A34A', bg: '#D1FAE5', label: 'XLS' }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { icon: FolderZip, color: '#D97706', bg: '#FEF3C7', label: 'ZIP' }
  if (['txt', 'log'].includes(ext)) return { icon: TextSnippet, color: '#64748B', bg: '#F1F5F9', label: 'TXT' }
  return { icon: InsertDriveFile, color: '#94A3B8', bg: '#F1F5F9', label: ext?.toUpperCase() || 'FILE' }
}

const TicketDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useContext(Context)
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const draftKey = `ticket_draft_${id}`
  const [newComment, setNewComment] = useState(() => localStorage.getItem(draftKey) || '')
  const [sendingComment, setSendingComment] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [replyOpen, setReplyOpen] = useState(false)
  const markdownEditorRef = React.useRef(null)
  const fileInputRef = useRef(null)
  const commentsEndRef = useRef(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackForced, setFeedbackForced] = useState(false)
  const [feedbackRating, setFeedbackRating] = useState(null)
  const [feedbackReason, setFeedbackReason] = useState('')
  const [feedbackComment, setFeedbackComment] = useState('')
  const [feedbackSending, setFeedbackSending] = useState(false)

  const { isRecording, formattedTime, startRecording, stopRecording, cancelRecording } = useAudioRecorder({
    onRecordingComplete: (file) => setPendingFiles(prev => [...prev, file]),
    onError: (msg) => enqueueSnackbar(msg, { variant: 'error' }),
  })

  const fetchTicket = () => {
    api.get(`tickets/${id}/`)
      .then(res => setTicket(res.data))
      .catch(err => {
        console.error(err)
        enqueueSnackbar('Erro ao carregar ticket.', { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTicket()
  }, [id])

  // Abrir modal de feedback automaticamente se resolvido e sem avaliação
  useEffect(() => {
    if (ticket && ticket.status === 'RESOLVED' && !ticket.feedback && !user?.is_superuser) {
      setFeedbackForced(true)
      setFeedbackOpen(true)
    }
  }, [ticket?.id, ticket?.status, ticket?.feedback])

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    const maxSize = 10 * 1024 * 1024
    const validFiles = []

    selectedFiles.forEach(file => {
      if (file.size > maxSize) {
        enqueueSnackbar(`"${file.name}" excede o limite de 10MB.`, { variant: 'warning' })
      } else {
        validFiles.push(file)
      }
    })

    setPendingFiles(prev => [...prev, ...validFiles])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleChangeStatus = async (newStatus) => {
    try {
      await api.post(`tickets/${id}/change-status/`, { status: newStatus })
      fetchTicket()
      enqueueSnackbar(
        newStatus === 'RESOLVED' ? 'Ticket marcado como resolvido!' : 'Ticket reaberto!',
        { variant: 'success' }
      )
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao alterar status.', { variant: 'error' })
    }
  }

  const handleResolveClick = () => {
    if (ticket?.feedback) {
      handleChangeStatus('RESOLVED')
    } else {
      setFeedbackOpen(true)
    }
  }

  const handleFeedbackSubmit = async () => {
    if (!feedbackRating) {
      enqueueSnackbar('Selecione uma avaliação.', { variant: 'warning' })
      return
    }
    if (feedbackRating <= 3 && !feedbackReason) {
      enqueueSnackbar('Selecione um motivo para sua experiência.', { variant: 'warning' })
      return
    }
    setFeedbackSending(true)
    try {
      const data = { rating: feedbackRating }
      if (feedbackReason) data.reason = feedbackReason
      if (feedbackComment) data.comment = feedbackComment
      await api.post(`tickets/${id}/feedback/`, data)
      setFeedbackOpen(false)
      fetchTicket()
      enqueueSnackbar('Obrigado pelo seu feedback! Ticket resolvido.', { variant: 'success' })
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao enviar feedback.', { variant: 'error' })
    } finally {
      setFeedbackSending(false)
    }
  }

  const handleSendComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() && pendingFiles.length === 0) return

    setSendingComment(true)
    try {
      // Criar comentário (com mensagem ou texto padrão para anexos)
      const message = newComment.trim() || (pendingFiles.length > 0 ? '' : '')
      const commentRes = await api.post(`tickets/${id}/comments/`, {
        ticket: id,
        message: message || (pendingFiles.some(f => isAudioMimeType(f.type)) ? 'Mensagem de áudio' : 'Anexo enviado'),
        is_internal: false,
      })
      const commentId = commentRes.data?.id

      // Link inline image attachments to comment
      if (commentId && markdownEditorRef.current?.getInlineAttachmentIds) {
        const inlineIds = markdownEditorRef.current.getInlineAttachmentIds()
        for (const attId of inlineIds) {
          try {
            await api.patch(`tickets/attachments/${attId}/`, { comment: commentId })
          } catch (e) { /* ignore */ }
        }
      }

      // Enviar arquivos vinculados ao comentário
      for (const file of pendingFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('ticket', id)
        if (commentId) formData.append('comment', commentId)
        await api.post(`tickets/${id}/attachments/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }

      setNewComment('')
      localStorage.removeItem(draftKey)
      setPendingFiles([])
      setReplyOpen(false)
      fetchTicket()
      enqueueSnackbar('Enviado!', { variant: 'success' })
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao enviar. Tente novamente.', { variant: 'error' })
    } finally {
      setSendingComment(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress sx={{ color: '#64748B' }} />
      </Box>
    )
  }

  if (!ticket) return null

  const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.OPEN
  const priorityStyle = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.MEDIUM
  const isClosed = ticket.status === 'RESOLVED'

  // Combinar descrição original como primeiro "comentário" + comentários reais
  const allMessages = [
    {
      id: 'description',
      user: ticket.created_by,
      message: ticket.description,
      created_at: ticket.created_at,
      attachments: ticket.attachments?.filter(a => !a.comment) || [],
      is_opening: true,
    },
    ...(ticket.comments || []),
  ]

  const renderAttachments = (attachments) => {
    if (!attachments?.length) return null
    const images = attachments.filter(att => /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic)$/i.test(att.filename || att.file))
    const audios = attachments.filter(att => isAudioFile(att.filename || att.file))
    const others = attachments.filter(att => !isAudioFile(att.filename || att.file) && !/\.(jpg|jpeg|png|gif|webp|bmp|svg|heic)$/i.test(att.filename || att.file))
    return (
      <Box mt={1.5}>
        <Divider sx={{ mb: 1.5 }} />
        {images.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
            {images.map(att => (
              <Box key={att.id} onClick={() => setImagePreview(att)} sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #E2E8F0', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                <Box component="img" src={att.file} sx={{ width: 120, height: 90, objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Box>
        )}
        {audios.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} mb={others.length > 0 ? 1.5 : 0}>
            {audios.map(att => (
              <AudioPlayer key={att.id} src={att.file} />
            ))}
          </Box>
        )}
        {others.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {others.map(att => {
              const info = getFileTypeInfo(att.filename || att.file)
              const FileIcon = info.icon
              return (
                <Tooltip key={att.id} title={att.filename || 'Arquivo'} arrow>
                  <Box
                    component="a"
                    href={att.file}
                    target="_blank"
                    sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      width: 64, height: 64, borderRadius: 2, bgcolor: info.bg, border: '1px solid #E2E8F0',
                      textDecoration: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      '&:hover': { transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}
                  >
                    <FileIcon sx={{ fontSize: 24, color: info.color }} />
                    <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: info.color, mt: 0.25 }}>{info.label}</Typography>
                  </Box>
                </Tooltip>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/suporte')} sx={{ color: '#64748B' }}>
          Voltar
        </Button>
      </Box>

      {/* Title bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="body2" sx={{ color: '#94A3B8', fontFamily: 'monospace', fontWeight: 600 }}>
            {ticket.protocol}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
            {ticket.title}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip label={priorityStyle.label} size="small" sx={{ bgcolor: priorityStyle.bg, color: priorityStyle.color, fontWeight: 600 }} />
          <Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }} />
        </Box>
      </Box>

      <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
        {/* Main content */}
        <Box flex={1}>

          {/* Messages as individual cards */}
          <Box display="flex" flexDirection="column-reverse" gap={2}>
            {allMessages.map(msg => {
              const isStaff = msg.user?.is_superuser
              return (
                <Card key={msg.id} sx={{ border: msg.is_opening ? '1px solid #E2E8F0' : 'none' }}>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={msg.user?.photo || undefined} sx={{ width: 28, height: 28, bgcolor: isStaff ? '#475569' : '#94A3B8', fontSize: 12, ...(isStaff && { border: '2px solid #0073ea' }) }}>
                          {msg.user?.first_name?.[0] || 'U'}
                        </Avatar>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#1E293B', lineHeight: 1 }}>
                            {msg.user?.first_name} {msg.user?.last_name}
                          </Typography>
                          {isStaff && (
                            <Tooltip title="Equipe Bloco B3" arrow placement="top">
                              <Verified sx={{ fontSize: 16, color: '#0073ea', cursor: 'default', display: 'block' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {msg.is_opening && (
                          <Chip label="Abertura" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#DBEAFE', color: '#1E40AF' }} />
                        )}
                      </Box>
                      <Tooltip title={moment(msg.created_at).format('DD/MM/YYYY [às] HH:mm')} arrow placement="top">
                        <Typography variant="caption" sx={{ color: '#94A3B8', cursor: 'default' }}>
                          {moment(msg.created_at).fromNow()}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* Body */}
                    <Box sx={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7, '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 }, '& ul, & ol': { m: 0, pl: 2.5, mb: 1 }, '& code': { bgcolor: '#F1F5F9', px: 0.5, py: 0.25, borderRadius: 0.5, fontSize: '0.85em', fontFamily: 'monospace' }, '& pre': { bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1, overflow: 'auto', mb: 1 }, '& pre code': { bgcolor: 'transparent', p: 0 }, '& a': { color: '#0073ea', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }, '& blockquote': { borderLeft: '3px solid #CBD5E1', pl: 1.5, ml: 0, color: '#64748B' }, '& strong': { fontWeight: 600 } }}>
                      <Markdown remarkPlugins={[remarkBreaks]} components={{
                        img: ({ src, alt }) => (
                          <Box
                            component="img"
                            src={src}
                            alt={alt || ''}
                            onClick={(e) => { e.stopPropagation(); setImagePreview({ file: src, filename: alt || 'imagem' }) }}
                            sx={{ maxWidth: 600, maxHeight: 400, borderRadius: 1.5, border: '1px solid #E2E8F0', cursor: 'pointer', display: 'block', my: 1, '&:hover': { opacity: 0.85 } }}
                          />
                        ),
                      }}>{msg.message}</Markdown>
                    </Box>

                    {/* Attachments */}
                    {renderAttachments(msg.attachments)}
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          {/* Action card */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5 }}>Ações</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {!isClosed && (
                  <>
                    <Button fullWidth variant="contained" startIcon={<Reply />} onClick={() => setReplyOpen(true)} sx={{ bgcolor: '#475569', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#334155' } }}>
                      Responder ticket
                    </Button>
                    <Divider sx={{ my: 0.5 }} />
                  </>
                )}
                {isClosed ? (
                  <Button fullWidth variant="contained" startIcon={<Refresh />} onClick={() => handleChangeStatus('OPEN')} sx={{ bgcolor: '#475569', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#334155' } }}>
                    Reabrir ticket
                  </Button>
                ) : (
                  <Button fullWidth variant="contained" startIcon={<CheckCircle />} onClick={handleResolveClick} sx={{ bgcolor: '#16A34A', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#15803D' } }}>
                    Finalizar ticket
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Details card */}
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>Detalhes</Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Status</Typography>
                  <Box mt={0.5}><Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: 12 }} /></Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Categoria</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><Category sx={{ fontSize: 16, color: '#64748B' }} /><Typography variant="body2" sx={{ color: '#334155' }}>{ticket.category?.name}</Typography></Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Criado por</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><Person sx={{ fontSize: 16, color: '#64748B' }} /><Typography variant="body2" sx={{ color: '#334155' }}>{ticket.created_by?.first_name} {ticket.created_by?.last_name}</Typography></Box>
                </Box>
                {ticket.assigned_to && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Atribuído a</Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><Person sx={{ fontSize: 16, color: '#64748B' }} /><Typography variant="body2" sx={{ color: '#334155' }}>{ticket.assigned_to?.first_name} {ticket.assigned_to?.last_name}</Typography></Box>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Criado em</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><AccessTime sx={{ fontSize: 16, color: '#64748B' }} /><Typography variant="body2" sx={{ color: '#334155' }}>{moment(ticket.created_at).format('DD/MM/YYYY HH:mm')}</Typography></Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>
                    Tempo do suporte
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                    <AccessTime sx={{ fontSize: 16, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : '#64748B' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : '#334155' }}>
                      {ticket.support_time || ticket.elapsed_time}
                    </Typography>
                  </Box>
                </Box>
                {ticket.due_date && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Prazo (SLA)</Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><AccessTime sx={{ fontSize: 16, color: moment(ticket.due_date).isBefore(moment()) ? '#DC2626' : '#64748B' }} /><Typography variant="body2" sx={{ color: moment(ticket.due_date).isBefore(moment()) ? '#DC2626' : '#334155', fontWeight: moment(ticket.due_date).isBefore(moment()) ? 600 : 400 }}>{moment(ticket.due_date).format('DD/MM/YYYY HH:mm')}</Typography></Box>
                  </Box>
                )}
                {ticket.closed_at && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Finalizado em</Typography>
                    <Typography variant="body2" sx={{ color: '#334155', mt: 0.25 }}>{moment(ticket.closed_at).format('DD/MM/YYYY HH:mm')}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Empresa</Typography>
                  <Typography variant="body2" sx={{ color: '#334155', mt: 0.25 }}>{ticket.condo?.name}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

        </Box>
      </Box>

      {/* Reply modal */}
      <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} fullWidth maxWidth="md">
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Responder</Typography>
            <IconButton size="small" onClick={() => setReplyOpen(false)}><Close /></IconButton>
          </Box>

          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} multiple accept="image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar" />

          {/* Pending files preview */}
          {pendingFiles.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
              {pendingFiles.map((file, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  {isAudioMimeType(file.type) ? (
                    <AudioPlayer src={URL.createObjectURL(file)} />
                  ) : file.type?.startsWith('image/') ? (
                    <Box component="img" src={URL.createObjectURL(file)} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                  ) : (
                    <InsertDriveFile sx={{ fontSize: 24, color: '#94A3B8' }} />
                  )}
                  {!isAudioMimeType(file.type) && (
                    <Box minWidth={0}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#334155', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 10 }}>{(file.size / 1024).toFixed(0)} KB</Typography>
                    </Box>
                  )}
                  <IconButton size="small" onClick={() => handleRemovePendingFile(index)} sx={{ color: '#94A3B8', p: 0.25 }}><Close sx={{ fontSize: 14 }} /></IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Editor */}
          <MarkdownEditor
            ref={markdownEditorRef}
            value={newComment}
            onChange={(val) => { setNewComment(val); localStorage.setItem(draftKey, val) }}
            placeholder="Digite sua resposta..."
            disabled={false}
            ticketId={id}
            showAIImprove={false}
          />

          <Divider sx={{ my: 2 }} />

          {/* Actions row */}
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
            {isRecording ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FiberManualRecord sx={{ fontSize: 14, color: '#EF4444', animation: 'pulse 1.2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#EF4444', fontFamily: 'monospace' }}>{formattedTime}</Typography>
                <Tooltip title="Parar e salvar"><IconButton size="small" onClick={stopRecording} sx={{ color: '#0073ea' }}><Stop /></IconButton></Tooltip>
                <Tooltip title="Cancelar"><IconButton size="small" onClick={cancelRecording} sx={{ color: '#94A3B8' }}><Close /></IconButton></Tooltip>
              </Box>
            ) : (
              <>
                <Tooltip title="Anexar arquivos"><IconButton onClick={() => fileInputRef.current?.click()} disabled={sendingComment} sx={{ color: '#94A3B8' }}><AttachFile /></IconButton></Tooltip>
                <Tooltip title="Gravar áudio"><IconButton onClick={startRecording} disabled={sendingComment} sx={{ color: '#94A3B8' }}><Mic /></IconButton></Tooltip>
              </>
            )}
            <Button
              variant="contained"
              startIcon={sendingComment ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={handleSendComment}
              disabled={sendingComment || isRecording || (!newComment.trim() && pendingFiles.length === 0)}
              sx={{ bgcolor: '#475569', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#334155' }, ml: 1 }}
            >
              Enviar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Image preview modal */}
      <Dialog open={!!imagePreview} onClose={() => setImagePreview(null)} maxWidth={false} PaperProps={{ sx: { bgcolor: '#fff', maxWidth: '92vw', borderRadius: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {imagePreview?.filename || 'Imagem'}
          </Typography>
          <IconButton size="small" onClick={() => setImagePreview(null)}><Close sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#F8FAFC' }}>
          <Box component="img" src={imagePreview?.file} sx={{ maxWidth: '88vw', maxHeight: '78vh', borderRadius: 1, objectFit: 'contain' }} />
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={feedbackOpen} onClose={feedbackForced ? undefined : () => setFeedbackOpen(false)} maxWidth="sm" fullWidth disableEscapeKeyDown={feedbackForced}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          Compartilhar seu feedback conosco
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
            Sua opinião é super importante pra gente continuar melhorando! ✨ Como você avalia a qualidade do atendimento que recebeu? 🌟
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            {[
              { value: 5, label: 'Ótimo', icon: <SentimentVerySatisfied />, color: '#16A34A' },
              { value: 4, label: 'Bom', icon: <SentimentSatisfied />, color: '#65A30D' },
              { value: 3, label: 'Regular', icon: <SentimentNeutral />, color: '#D97706' },
              { value: 2, label: 'Ruim', icon: <SentimentDissatisfied />, color: '#EA580C' },
              { value: 1, label: 'Péssimo', icon: <SentimentVeryDissatisfied />, color: '#DC2626' },
            ].map(opt => (
              <Box
                key={opt.value}
                onClick={() => setFeedbackRating(opt.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  p: 1.5, borderRadius: 2, cursor: 'pointer',
                  border: feedbackRating === opt.value ? `2px solid ${opt.color}` : '2px solid #E2E8F0',
                  bgcolor: feedbackRating === opt.value ? `${opt.color}10` : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: opt.color, bgcolor: `${opt.color}08` },
                }}
              >
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: feedbackRating === opt.value ? opt.color : '#F1F5F9',
                  color: feedbackRating === opt.value ? '#fff' : opt.color,
                  fontWeight: 700, fontSize: 18, transition: 'all 0.15s',
                }}>
                  {opt.value}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: feedbackRating === opt.value ? opt.color : '#475569' }}>
                  {React.cloneElement(opt.icon, { sx: { fontSize: 22 } })}
                  <Typography sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {feedbackRating && feedbackRating <= 3 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Selecione um motivo para sua experiência</InputLabel>
              <Select
                value={feedbackReason}
                label="Selecione um motivo para sua experiência"
                onChange={(e) => setFeedbackReason(e.target.value)}
              >
                <MenuItem value="slow">A resolução do problema foi muito demorada</MenuItem>
                <MenuItem value="not_resolved">O problema não foi resolvido</MenuItem>
                <MenuItem value="unclear">As informações fornecidas não foram claras ou úteis</MenuItem>
                <MenuItem value="rude">Atendimento não foi cordial</MenuItem>
                <MenuItem value="other">Outro motivo</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Dê sua opinião sobre o suporte recebido"
            placeholder="Conte-nos sobre sua experiência..."
            value={feedbackComment}
            onChange={(e) => setFeedbackComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          {!feedbackForced && (
            <Button onClick={() => setFeedbackOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>
              Cancelar
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleFeedbackSubmit}
            disabled={!feedbackRating || feedbackSending}
            sx={{ textTransform: 'none', bgcolor: '#16A34A', fontWeight: 600, '&:hover': { bgcolor: '#15803D' } }}
          >
            {feedbackSending ? <CircularProgress size={20} color="inherit" /> : 'Enviar feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TicketDetail
